import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PaneHeader } from '@/components/settings/pane-header';
import { NumberPreferenceRow, PreferenceRow, SelectPreferenceRow } from '@/components/settings/settings-controls';
import { NotificationsCard } from '@/components/settings/notifications-card';
import { TimezoneCard } from '@/components/settings/timezone-card';
import { useUserPreferences } from '@/hooks/use-user-preferences';
import { fetchSessionLockSettings, putSessionLockSettings } from '@/lib/api/settings';
import { isWebMode } from '@/lib/config/web-mode';
import { withAppTimeZone } from '@/lib/timezone';
import { setUserPreferences } from '@/lib/stores/user-preference-store';
import { APP_CONFIG } from '@/lib/config/app-config';

// Tenant-wide idle auto-lock editor (own fetch/save: session-lock has its own canEdit gate,
// enforced server-side regardless). Minutes as a string so clearing the field mid-edit works.
function SecurityCard() {
  const [idleLockMinutes, setIdleLockMinutes] = useState('10');
  const [canEdit, setCanEdit] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchSessionLockSettings()
      .then((res) => {
        if (cancelled) return;
        setIdleLockMinutes(String(res.idleLockMinutes));
        setCanEdit(Boolean(res.canEdit));
      })
      .catch(() => {
        /* keep the default; the card still renders (read-only until a fetch succeeds) */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSave = async () => {
    if (!canEdit || saving) return;
    const minutes = Number(idleLockMinutes);
    if (!Number.isFinite(minutes) || minutes < 0 || minutes > 1440) {
      toast.error('Auto-lock must be between 0 and 1440 minutes.');
      return;
    }
    setSaving(true);
    try {
      const res = await putSessionLockSettings({ idleLockMinutes: minutes });
      setIdleLockMinutes(String(res.idleLockMinutes));
      setCanEdit(Boolean(res.canEdit));
      toast.success('Security settings saved for everyone.');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not save security settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="text-xl">Security</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">
          Shared with your whole team — changes here apply to everyone.
          {!canEdit ? ' Only eligible staff can edit these settings.' : ''}
        </p>
        <div className="flex items-center justify-between gap-4 py-1">
          <div className="space-y-0.5">
            <p className="text-sm font-medium text-foreground">Auto-lock after inactivity</p>
            <p className="text-xs text-muted-foreground">
              Lock the app after this many minutes without activity. 0 = never lock.
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-1.5">
            <Input
              type="number"
              inputMode="numeric"
              min={0}
              max={1440}
              value={idleLockMinutes}
              disabled={!canEdit || saving}
              aria-label="Auto-lock after inactivity"
              onChange={(e) => setIdleLockMinutes(e.target.value)}
              className="h-8 w-20 text-right tabular-nums"
            />
            <span className="text-xs text-muted-foreground">min</span>
          </div>
        </div>
        <div className="flex justify-end">
          <Button type="button" size="sm" onClick={handleSave} disabled={!canEdit || saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// Device + shared preferences, relocated verbatim from the old flat settings page into the
// sub-rail. Theme is owned by App.jsx (theme/onSelectTheme); device prefs come from the
// user-preference store; the bot-ack and patient-reply windows are tenant-wide shared settings
// owned by the parent SettingsPage (botAck / patientReply / their onCommit / canEditShared).
export function PreferencesSubPage({
  theme,
  onSelectTheme,
  canEditShared,
  botAck,
  onCommitBotAck,
  patientReply,
  onCommitPatientReply,
}) {
  const preferences = useUserPreferences();
  const [platform, setPlatform] = useState('');
  const [checkingUpdates, setCheckingUpdates] = useState(false);
  const [installingUpdate, setInstallingUpdate] = useState(false);
  const [updaterInfo, setUpdaterInfo] = useState({
    enabled: false,
    status: 'idle',
    message: '',
    lastCheckedAt: 0,
    releaseName: '',
  });

  useEffect(() => {
    let cancelled = false;

    window.electronAPI
      ?.getPlatform?.()
      .then((value) => {
        if (!cancelled) {
          setPlatform(String(value || ''));
        }
      })
      .catch(() => {
        // Non-fatal: keep empty platform and hide updates card if unsupported cannot be confirmed.
      });

    const applyUpdaterState = (payload) => {
      if (cancelled || !payload) {
        return;
      }
      setUpdaterInfo((prev) => ({
        ...prev,
        ...payload,
      }));
    };

    window.electronAPI
      ?.getUpdaterState?.()
      .then((state) => {
        applyUpdaterState(state);
      })
      .catch(() => {
        /* non-fatal: updates card still works via explicit checks */
      });

    const unsubscribe = window.electronAPI?.onUpdaterStatus?.(applyUpdaterState);

    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  }, []);

  const updatePlatforms = Array.isArray(APP_CONFIG.UPDATE_FOR_PLATFORMS)
    ? APP_CONFIG.UPDATE_FOR_PLATFORMS
    : [];
  const isUpdatesCardVisible = Boolean(platform) && updatePlatforms.includes(platform);

  const formatLastChecked = (ts) => {
    if (!ts) {
      return 'Never';
    }
    try {
      return new Date(ts).toLocaleString(undefined, withAppTimeZone());
    } catch {
      return 'Unknown';
    }
  };

  const manualCheckBlockReason =
    updaterInfo.status === 'checking'
      ? 'Update check in progress.'
      : updaterInfo.status === 'available'
        ? 'Update is downloading in the background. It may take a few minutes.'
        : updaterInfo.status === 'downloaded'
          ? 'Update is ready to install.'
          : updaterInfo.status === 'installing'
            ? 'Update installation is in progress.'
            : '';
  const isManualCheckBlocked = Boolean(manualCheckBlockReason);

  const handleCheckForUpdates = async () => {
    if (checkingUpdates) return;

    if (isManualCheckBlocked) {
      toast.info(manualCheckBlockReason);
      return;
    }

    setCheckingUpdates(true);
    try {
      const state = await window.electronAPI?.getUpdaterState?.();
      if (state) {
        setUpdaterInfo((prev) => ({
          ...prev,
          ...state,
        }));
      }
      if (!state?.enabled) {
        toast.info(state?.message || 'Auto-update is not available in this environment.');
        return;
      }

      const result = await window.electronAPI?.checkForUpdates?.({ manual: true });
      if (result?.ok === false) {
        toast.info(result?.reason || 'Could not start update check.');
        return;
      }

      toast.message('Checking for updates...');
    } catch {
      toast.error('Could not check for updates.');
    } finally {
      setCheckingUpdates(false);
    }
  };

  const handleInstallNow = async () => {
    if (installingUpdate) {
      return;
    }
    setInstallingUpdate(true);
    try {
      const result = await window.electronAPI?.quitAndInstallUpdate?.();
      if (!result?.ok) {
        toast.error(result?.reason || 'Could not start update installation.');
      }
    } catch {
      toast.error('Could not start update installation.');
    } finally {
      setInstallingUpdate(false);
    }
  };

  return (
    <div className="flex h-full min-h-0 flex-col">
      <PaneHeader title="Preferences" description="Theme, account preferences, and shared Messages settings." />
      <ScrollArea className="min-h-0 flex-1" viewportClassName="p-6">
        <div className="mx-auto flex max-w-3xl flex-col gap-6">
          <Card className="w-full">
            <CardHeader>
              <CardTitle className="text-xl">Appearance</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">Choose your preferred theme.</p>
              <div className="flex flex-wrap gap-3">
                <Button
                  type="button"
                  variant={theme === 'light' ? 'default' : 'outline'}
                  onClick={() => onSelectTheme?.('light')}
                >
                  Light mode
                </Button>
                <Button
                  type="button"
                  variant={theme === 'dark' ? 'default' : 'outline'}
                  onClick={() => onSelectTheme?.('dark')}
                >
                  Dark mode
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card className="w-full">
            <CardHeader>
              <CardTitle className="text-xl">Account preferences</CardTitle>
            </CardHeader>
            <CardContent className="space-y-1">
              <p className="text-xs text-muted-foreground">
                Saved to your account — these follow you to any workstation.
              </p>
              <PreferenceRow
                title="Hide Sundays"
                description="Hide the Sunday column from calendar grids (slots, week, and month)."
                checked={preferences.calendarHideSunday}
                onChange={(next) => setUserPreferences({ calendarHideSunday: next })}
              />
              <PreferenceRow
                title="Combine WhatsApp threads per patient"
                description="Merge a patient's WhatsApp chats from every clinic number into one conversation, with per-clinic marking and a reply-from picker."
                checked={preferences.mergedWhatsAppView}
                onChange={(next) => setUserPreferences({ mergedWhatsAppView: next })}
              />
              <PreferenceRow
                title="Combine WhatsApp group chats"
                description="Show one row per WhatsApp group instead of one per clinic number. Opening it marks every clinic's copy read, and you pick which number replies."
                checked={preferences.mergedWaGroupsView}
                onChange={(next) => setUserPreferences({ mergedWaGroupsView: next })}
              />
            </CardContent>
          </Card>

          <TimezoneCard />

          {/* Browser notification permission — Electron's is granted by the main process. */}
          {isWebMode() ? <NotificationsCard /> : null}

          <Card className="w-full">
            <CardHeader>
              <CardTitle className="text-xl">Shared settings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-xs text-muted-foreground">
                Shared with your whole team — changes here apply to everyone.
                {!canEditShared ? ' Only eligible staff can edit these settings.' : ''}
              </p>
              <div className="space-y-1">
                <NumberPreferenceRow
                  title="Surface unanswered bot replies after"
                  description="Show a thread under “Unanswered bot replies” once the bot's last message is this old."
                  value={botAck.thresholdMinutes}
                  min={1}
                  max={1440}
                  unit="min"
                  disabled={!canEditShared}
                  onCommit={(raw) => onCommitBotAck({ thresholdMinutes: Number(raw) })}
                />
                <NumberPreferenceRow
                  title="Stop showing them after"
                  description="Ignore the thread once the bot's last message is older than this."
                  value={botAck.maxHours}
                  min={1}
                  max={720}
                  unit="hr"
                  disabled={!canEditShared}
                  onCommit={(raw) => onCommitBotAck({ maxHours: Number(raw) })}
                />
              </div>
              <div className="space-y-1">
                <NumberPreferenceRow
                  title="Surface unanswered patient replies after"
                  description="Show a thread under “Unanswered patient reply” once the patient's last message is this old."
                  value={patientReply.thresholdMinutes}
                  min={1}
                  max={1440}
                  unit="min"
                  disabled={!canEditShared}
                  onCommit={(raw) => onCommitPatientReply({ thresholdMinutes: Number(raw) })}
                />
                <NumberPreferenceRow
                  title="Stop showing them after"
                  description="Ignore the thread once the patient's last message is older than this. Keep this generous — a patient nobody answered disappears from “Needs you” at this point."
                  value={patientReply.maxHours}
                  min={1}
                  max={720}
                  unit="hr"
                  disabled={!canEditShared}
                  onCommit={(raw) => onCommitPatientReply({ maxHours: Number(raw) })}
                />
              </div>
            </CardContent>
          </Card>

          <SecurityCard />

          {/* Auto-updates are Electron-only; a browser tab just reloads. */}
          {!isWebMode() && isUpdatesCardVisible && (
            <Card className="w-full">
              <CardHeader>
                <CardTitle className="text-xl">Updates</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Check if a new version of the application is available.
                </p>
                <p className="text-xs text-muted-foreground">
                  Last checked: {formatLastChecked(updaterInfo.lastCheckedAt)}
                </p>
                {updaterInfo.status === 'downloaded' ? (
                  <div className="space-y-2">
                    <p className="text-xs text-amber-600 dark:text-amber-400">
                      Update ready to install{updaterInfo.releaseName ? `: ${updaterInfo.releaseName}` : ''}.
                    </p>
                    <Button
                      type="button"
                      size="sm"
                      variant="default"
                      onClick={handleInstallNow}
                      disabled={installingUpdate}
                    >
                      {installingUpdate ? 'Starting install…' : 'Install now'}
                    </Button>
                  </div>
                ) : null}
                {updaterInfo.status === 'installing' ? (
                  <p className="text-xs text-amber-600 dark:text-amber-400">Installing update...</p>
                ) : null}
                {isManualCheckBlocked ? (
                  <p className="text-xs text-muted-foreground">{manualCheckBlockReason}</p>
                ) : null}
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={handleCheckForUpdates}
                  disabled={checkingUpdates || isManualCheckBlocked}
                >
                  {checkingUpdates ? 'Checking…' : 'Check for updates'}
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}